// =============================================================================
// TabBar — Chrome-style open-screens tab strip
// =============================================================================
// Ported from the SWAS ERP employee shell so SkyEye behaves the same way: each
// screen you open from the sidebar becomes a tab. Click a tab to switch to it,
// click its × to close it. Right-click a tab for a context menu: Close / Close
// Others / Close All.
//
// Drag-to-reorder is a custom POINTER drag (not native HTML5 DnD) built the way
// Chrome does it, so it is buttery smooth:
//   • The DOM order does NOT change while dragging (zero React re-renders per
//     move). The grabbed tab follows the pointer via a transform; the OTHER tabs
//     slide via CSS-transitioned transforms to open a gap where it will land.
//   • The new order is committed ONCE, on drop, with a short settle animation.
//   • The grabbed tab is CLAMPED to the bar — it can never float outside.
//   • Dragging to either edge AUTO-SCROLLS the strip so off-screen tabs can be
//     reached.
//
//   <TabBar tabs={[{ key, label }]} activeKey={k}
//           onActivate={fn} onClose={fn} onCloseOthers={fn} onCloseAll={fn}
//           onReorder={(fromKey, toIndex) => …} />
// =============================================================================

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import "../assets/css/tabbar.css";

const SLIDE_MS = 170;                       // tab slide / settle duration
const SLIDE_EASE = "cubic-bezier(0.2, 0, 0, 1)";
const DRAG_THRESHOLD = 4;                    // px of travel before a press = a drag
const EDGE_ZONE = 44;                        // px from a bar edge that auto-scrolls
const SCROLL_STEP = 16;                      // px/frame auto-scroll speed
const MENU_WIDTH = 184;                      // keep in sync with .sk-tab-menu min-width

export default function TabBar({
  tabs = [],
  activeKey,
  onActivate,
  onClose,
  onCloseOthers,
  onCloseAll,
  isCollapsed,
  isMobile,
  onReorder,
}) {
  // Right-click context menu: { key, x, y } at the cursor, or null when closed.
  const [menu, setMenu] = useState(null);

  const barRef = useRef(null);          // the .sk-tabbar container
  const tabEls = useRef(new Map());     // key -> tab element
  const prevOffsets = useRef(new Map()); // key -> offsetLeft (FLIP for open/close)
  const drag = useRef(null);            // live drag state (see onTabPointerDown)
  const geom = useRef(null);            // geometry cached for the whole drag
  const settle = useRef(null);          // { key, oldLeft } drop settle for the effect
  const lastX = useRef(0);              // last pointer clientX
  const autoRAF = useRef(0);            // auto-scroll rAF id (0 = idle)
  const suppressClick = useRef(false);  // swallow the click that ends a drag
  const [dragKey, setDragKey] = useState(null);

  const setTabEl = (key) => (el) => {
    if (el) tabEls.current.set(key, el);
    else tabEls.current.delete(key);
  };

  // ---- geometry (measured ONCE at grab; the DOM order never changes mid-drag) -
  const buildGeom = () => {
    const bar = barRef.current;
    const d = drag.current;
    if (!bar || !d) return;
    const rect = bar.getBoundingClientRect();
    const slots = tabs.map((t) => {
      const el = tabEls.current.get(t.key);
      return { key: t.key, left: el ? el.offsetLeft : 0, width: el ? el.offsetWidth : 0 };
    });
    const fromIndex = slots.findIndex((s) => s.key === d.key);
    const gap = slots.length > 1
      ? Math.max(0, slots[1].left - (slots[0].left + slots[0].width))
      : 6;
    geom.current = {
      barLeftVp: rect.left,
      clientW: bar.clientWidth,
      slots,
      fromIndex,
      unit: (slots[fromIndex] ? slots[fromIndex].width : 0) + gap, // space the tab occupies
    };
    // The non-dragged tabs get a transform transition so their shift slides.
    slots.forEach((s) => {
      if (s.key === d.key) return;
      const el = tabEls.current.get(s.key);
      if (el) el.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASE}`;
    });
  };

  // Shift the non-dragged tabs to open a gap at the current target index.
  const applyShifts = () => {
    const d = drag.current;
    const g = geom.current;
    if (!d || !g) return;
    g.slots.forEach((s, i) => {
      if (s.key === d.key) return;
      let shift = 0;
      if (d.curIndex > g.fromIndex && i > g.fromIndex && i <= d.curIndex) shift = -g.unit;
      else if (d.curIndex < g.fromIndex && i >= d.curIndex && i < g.fromIndex) shift = g.unit;
      const el = tabEls.current.get(s.key);
      if (el) el.style.transform = shift ? `translateX(${shift}px)` : "";
    });
  };

  // Place the grabbed tab under the pointer (clamped to the bar) and, from its
  // position, pick the target index. Pure transform writes — no layout thrash.
  const positionDrag = (pointerX) => {
    const d = drag.current;
    const g = geom.current;
    const bar = barRef.current;
    const el = d && tabEls.current.get(d.key);
    if (!el || !g || !bar) return;
    const scrollLeft = bar.scrollLeft;
    const fromSlot = g.slots[g.fromIndex];
    // Desired viewport-left of the tab, clamped so it stays inside the bar.
    let vpLeft = pointerX - d.grabDX;
    vpLeft = Math.max(g.barLeftVp, Math.min(g.barLeftVp + g.clientW - fromSlot.width, vpLeft));
    const naturalVpLeft = g.barLeftVp - scrollLeft + fromSlot.left;
    el.style.transform = `translateX(${vpLeft - naturalVpLeft}px)`;
    // Target index from the grabbed tab's centre in CONTENT coords (scroll-aware).
    const centerContent = (vpLeft - g.barLeftVp + scrollLeft) + fromSlot.width / 2;
    let curIndex = 0;
    for (let i = 0; i < g.slots.length - 1; i += 1) {
      const midpoint = (g.slots[i].left + g.slots[i].width / 2 + g.slots[i + 1].left + g.slots[i + 1].width / 2) / 2;
      if (centerContent > midpoint) curIndex = i + 1;
    }
    if (curIndex !== d.curIndex) { d.curIndex = curIndex; applyShifts(); }
  };

  // ---- edge auto-scroll -----------------------------------------------------
  const stopAutoScroll = () => {
    if (autoRAF.current) cancelAnimationFrame(autoRAF.current);
    autoRAF.current = 0;
  };
  const runAutoScroll = () => {
    const d = drag.current;
    const bar = barRef.current;
    if (!d || !bar || !d.scrollDir) { autoRAF.current = 0; return; }
    const before = bar.scrollLeft;
    bar.scrollLeft = before + d.scrollDir * SCROLL_STEP;
    if (bar.scrollLeft === before) { d.scrollDir = 0; autoRAF.current = 0; return; } // hit the end
    positionDrag(lastX.current); // new scroll → re-place + maybe re-target
    autoRAF.current = requestAnimationFrame(runAutoScroll);
  };
  const updateAutoScroll = (pointerX) => {
    const d = drag.current;
    const bar = barRef.current;
    const g = geom.current;
    if (!d || !bar || !g) return;
    const maxScroll = bar.scrollWidth - bar.clientWidth;
    let dir = 0;
    if (pointerX > g.barLeftVp + g.clientW - EDGE_ZONE && bar.scrollLeft < maxScroll - 0.5) dir = 1;
    else if (pointerX < g.barLeftVp + EDGE_ZONE && bar.scrollLeft > 0.5) dir = -1;
    d.scrollDir = dir;
    if (dir && !autoRAF.current) autoRAF.current = requestAnimationFrame(runAutoScroll);
    else if (!dir) stopAutoScroll();
  };

  // ---- pointer handlers -----------------------------------------------------
  const onTabPointerDown = (e, key) => {
    if (e.button !== 0) return;                        // left button only
    if (e.target.closest(".sk-tabbar-close")) return;  // let the × work
    suppressClick.current = false;
    const el = tabEls.current.get(key);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    drag.current = { key, pointerId: e.pointerId, grabDX: e.clientX - rect.left, startX: e.clientX, moved: false, curIndex: -1, scrollDir: 0 };
    lastX.current = e.clientX;
    try { el.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  };

  const onTabPointerMove = (e) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    lastX.current = e.clientX;
    if (!d.moved) {
      if (Math.abs(e.clientX - d.startX) < DRAG_THRESHOLD) return; // still a click
      d.moved = true;
      d.curIndex = -1;
      setDragKey(d.key);
      const el = tabEls.current.get(d.key);
      if (el) { el.style.transition = "none"; el.style.zIndex = "5"; }
      buildGeom();
    }
    positionDrag(e.clientX);
    updateAutoScroll(e.clientX);
  };

  const endPointer = (e) => {
    const d = drag.current;
    if (!d || (e && e.pointerId !== d.pointerId)) return;
    stopAutoScroll();
    const el = tabEls.current.get(d.key);
    try { if (el) el.releasePointerCapture(d.pointerId); } catch (_) { /* ignore */ }
    if (d.moved && el && geom.current) {
      suppressClick.current = true;
      const target = d.curIndex < 0 ? geom.current.fromIndex : d.curIndex;
      // Snapshot the grabbed tab's on-screen position; the settle effect slides it
      // into its final slot after the (single) commit. The shifted tabs already
      // sit in their new slots, so committing the order is seamless for them.
      settle.current = { key: d.key, oldLeft: el.getBoundingClientRect().left };
      if (target !== geom.current.fromIndex) onReorder?.(d.key, target);
    }
    drag.current = null;
    // Always re-render (batched with any onReorder) so the settle effect runs and
    // the dragging state clears.
    setDragKey(null);
  };

  // ---- FLIP: drop settle + open/close slide ---------------------------------
  useLayoutEffect(() => {
    const cur = new Map();
    tabEls.current.forEach((el, key) => { if (el) cur.set(key, el.offsetLeft); });

    // Drop settle: clear the shifted tabs (seamless — they already sit in their
    // new slots) and slide ONLY the grabbed tab from where it was to its slot.
    if (settle.current) {
      const { key, oldLeft } = settle.current;
      settle.current = null;
      tabEls.current.forEach((el, k) => {
        if (el && k !== key) { el.style.transition = ""; el.style.transform = ""; }
      });
      const el = tabEls.current.get(key);
      if (el && barRef.current) {
        el.style.transition = "none";
        el.style.transform = "";
        const newLeft = el.getBoundingClientRect().left;
        el.style.transform = `translateX(${oldLeft - newLeft}px)`;
        void barRef.current.offsetWidth; // eslint-disable-line no-unused-expressions
        el.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASE}`;
        el.style.transform = "";
        setTimeout(() => {
          el.style.zIndex = "";
          el.style.transition = "";
          el.style.transform = "";
        }, SLIDE_MS + 40);
      }
      prevOffsets.current = cur;
      return;
    }

    // Never run the open/close FLIP mid-drag (the tabs carry live shift transforms).
    if (drag.current && drag.current.moved) { prevOffsets.current = cur; return; }

    // Open/close a tab: slide the rest into place.
    const toPlay = [];
    cur.forEach((left, key) => {
      const el = tabEls.current.get(key);
      const prev = prevOffsets.current.get(key);
      if (el && prev != null) {
        const dx = prev - left;
        if (Math.abs(dx) > 0.5) {
          el.style.transition = "none";
          el.style.transform = `translateX(${dx}px)`;
          toPlay.push(el);
        }
      }
    });
    prevOffsets.current = cur;
    if (toPlay.length && barRef.current) {
      void barRef.current.offsetWidth; // eslint-disable-line no-unused-expressions
      toPlay.forEach((el) => {
        el.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASE}, background 0.15s, color 0.15s`;
        el.style.transform = "";
      });
    }
  });

  // Dismiss the context menu on any outside click, scroll, resize or Escape.
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => {
      if (e.key === "Escape") setMenu(null);
    };
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  // Cancel any running auto-scroll if the component unmounts mid-drag.
  useEffect(() => () => stopAutoScroll(), []);

  if (!tabs.length) return null;

  const openMenu = (e, key) => {
    e.preventDefault();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setMenu({
      key,
      x: Math.min(e.clientX, vw - MENU_WIDTH - 8),
      y: Math.min(e.clientY, vh - 132),
    });
  };

  const run = (fn) => {
    const key = menu?.key;
    setMenu(null);
    if (fn) fn(key);
  };

  const hasOthers = tabs.length > 1;

  /* You cannot close your last tab. Closing it would land on HOME_PATH, which
     immediately opens the Dashboard tab — so the ✕ would either appear to do
     nothing or replace one lone tab with another. Hidden rather than disabled:
     a greyed-out ✕ on a single tab is a question nobody wants to answer. */
  const canClose = tabs.length > 1;

  return (
    <div
      className={`sk-tabbar${dragKey ? " is-reordering" : ""}${
        isCollapsed ? " collapsed" : ""
      }${isMobile ? " is-mobile" : ""}`}
      role="tablist"
      ref={barRef}
    >
      {tabs.map((t) => (
        <div
          key={t.key}
          ref={setTabEl(t.key)}
          role="tab"
          aria-selected={t.key === activeKey}
          className={`sk-tabbar-tab${t.key === activeKey ? " is-active" : ""}${t.key === dragKey ? " is-dragging" : ""}`}
          onPointerDown={(e) => onTabPointerDown(e, t.key)}
          onPointerMove={onTabPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onClick={() => {
            // A drag just ended on this tab — swallow its click, don't activate.
            if (suppressClick.current) { suppressClick.current = false; return; }
            onActivate(t.key);
          }}
          onContextMenu={(e) => openMenu(e, t.key)}
          title={t.label}
        >
          <span className="sk-tabbar-label">{t.label}</span>
          {canClose && (
            <button
              type="button"
              className="sk-tabbar-close"
              aria-label={`Close ${t.label}`}
              onClick={(e) => {
                e.stopPropagation();
                onClose(t.key);
              }}
            >
              <FiX />
            </button>
          )}
        </div>
      ))}

      {menu &&
        createPortal(
          <div
            className="sk-tab-menu"
            role="menu"
            style={{ top: menu.y, left: menu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="sk-tab-menu-item" role="menuitem" onClick={() => run(onClose)}>
              Close
            </button>
            <button
              type="button"
              className="sk-tab-menu-item"
              role="menuitem"
              disabled={!hasOthers}
              onClick={() => run(onCloseOthers)}
            >
              Close Others
            </button>
            <button type="button" className="sk-tab-menu-item" role="menuitem" onClick={() => run(onCloseAll)}>
              Close All
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
